import React, {useEffect, useState} from 'react';
import {useSelector} from 'react-redux';
import {useTranslation} from 'react-i18next';
import {useQuery} from '@tanstack/react-query';
import {QUERY_KEYS} from '../../../../utils/queryKeys';
import PresentationRoomsFilter from './PresentationRoomsFilter';
import RequestParamFilter from './RequestParamFilter';
import {Option} from '../../../components/common/Select';
import {selectedPresentationSelector} from '../../../../store/selectors/presentation.selectors';
import {fetchRoomsForPresentation} from '../../../../service/room.service';
import Label from '../../../../incase-domain/components/label/Label';

type FilterQueryType =
    | {[k: string]: string | string[]}
    | {roomId: string; groupIdentification: string | undefined; specificIdentification: string | undefined};

interface ReportsFilterProps {
    setFilterQuery: (filterQuery: FilterQueryType) => void;
}

function ReportsFilter({setFilterQuery}: ReportsFilterProps): JSX.Element | null {
    const {t} = useTranslation();
    const selectedPresentation = useSelector(selectedPresentationSelector);
    const presId = selectedPresentation._id;

    const initialParamsState =
        selectedPresentation?.requestParams?.reduce((acc, param) => {
            acc[param.key] = {
                label: 'Any',
                value: '',
            };
            return acc;
        }, {} as Record<string, Option>) || {};

    const [roomParams, setRoomParams] = useState<{roomId: string; identification?: string; group?: string}>();
    const [selectedParams, setSelectedParams] = useState<Record<string, Option | undefined>>(initialParamsState);
    const [fromDate, setFromDate] = useState<string>('');
    const [toDate, setToDate] = useState<string>('');

    useEffect(() => {
        const filterQuery: FilterQueryType = {
            ...Object.fromEntries(Object.entries(selectedParams).map(([key, option]) => [key, option?.value || ''])),
        };

        if (roomParams?.roomId) {
            filterQuery.roomId = roomParams.roomId;

            if (roomParams.group) {
                filterQuery.groupIdentification = roomParams.group;
            }

            if (roomParams.identification) {
                filterQuery.specificIdentification = roomParams.identification;
            }
        }

        if (fromDate) {
            filterQuery.from = new Date(fromDate).toISOString();
        }

        if (toDate) {
            const date = new Date(toDate);
            date.setHours(23, 59, 59, 999);
            filterQuery.to = date.toISOString();
        }

        setFilterQuery(filterQuery);
    }, [roomParams, selectedParams, fromDate, toDate, setFilterQuery]);

    const {data: rooms} = useQuery(QUERY_KEYS.roomsForPresentation.detail(presId), () => fetchRoomsForPresentation(presId), {
        keepPreviousData: true,
        enabled: !!presId,
        refetchOnMount: 'always',
    });

    return (
        <div className="c-reportFilter">
            {!!rooms?.length && (
                <>
                    <PresentationRoomsFilter roomParams={roomParams} setRoomParams={setRoomParams} rooms={rooms} />
                    <hr />
                </>
            )}
            {!!selectedPresentation?.requestParams?.length && (
                <>
                    <RequestParamFilter selectedParams={selectedParams} setSelectedParams={setSelectedParams} />
                    <hr />
                </>
            )}
            <div className="c-reportFilter__dateRange">
                <div>
                    <Label>{t('reportsPage.fromDate')}</Label>
                    <div className="c-sidepanel__input">
                        <input type="date" id="fromDate" value={fromDate} onChange={(e) => setFromDate(e.target.value)} />
                    </div>
                </div>
                <div>
                    <Label>{t('reportsPage.toDate')}</Label>
                    <div className="c-sidepanel__input">
                        <input type="date" id="toDate" value={toDate} onChange={(e) => setToDate(e.target.value)} />
                    </div>
                </div>
            </div>
        </div>
    );
}

export default ReportsFilter;
